import { MenuItem, Select, TextField, Typography } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { CustomTooltip } from 'components/facets'
import { Button } from 'components/facets/Button'
import { useGradingScale } from 'lib/hooks/useGradingScale'
import { ChangesInterface, useLocationStore } from 'lib/hooks/useLocationStore'
import { useEffect, useRef, useState } from 'react'
import { FormLabel } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import {
  CardExtraAttribute,
  CARD_LANGUAGES_WITH_FLAGS,
  CountryEdition,
  countryEditionMap,
  FINISH_TYPES,
  FINISH_TYPE_LABELS,
  GRADING_COMPANY_LABELS,
  InventoryArticle,
} from 'shared'
import { ExtraAttributesValuesAndImagesMapInterface } from 'utils/constants'
import { Filter } from '../FilterDock/Filter'
import EditConditionCell, { EditConditionCellRef } from './EditConditionCell'
import EditExtraAttributeCell, { EditExtraAttributesCellRef } from './EditExtraAttributeCell'
import EditLanguageCell, { EditLanguageCellRef } from './EditLanguageCell'
import './locations-panel.css'

export interface LocationsPanelRowProps {
  article: InventoryArticle
  location: any
  rowId: string
  gameInformations: ExtraAttributesValuesAndImagesMapInterface
  isSingle: boolean
}

export const LocationsPanelRow = ({
  article,
  location,
  rowId,
  gameInformations,
  isSingle,
}: LocationsPanelRowProps): JSX.Element => {
  const initialAttributes = {
    deltaQuantity: 0,
    condition: article.newCondition || article.condition,
    idLanguage: article.newIdLanguage || article.idLanguage,
    finishType: article.finishType,
    countryEdition: article.countryEdition,
    gradingCompany: article.graded.gradingCompany,
    grade: article.graded.grade,
  }
  const initialExtraAttributes = {
    isSigned: article.isSigned,
    isFirstEd: article.isFirstEd,
  }
  const [deltaQuantity, setDeltaQuantity] = useState(initialAttributes.deltaQuantity)
  const [quantityToEdit, setQuantityToEdit] = useState(location.quantity)
  const [newCondition, setNewCondition] = useState(initialAttributes.condition)
  const [newIdLanguage, setNewIdLanguage] = useState(initialAttributes.idLanguage)
  const [newSigned, setNewSigned] = useState(initialExtraAttributes.isSigned)
  const [newFirstEdition, setNewFirstEdition] = useState(initialExtraAttributes.isFirstEd)
  const [newFinishType, setNewFinishType] = useState(
    initialAttributes.finishType ?? FINISH_TYPES.REGULAR
  )
  const [newCountryEdition, setNewCountryEdition] = useState(
    initialAttributes.countryEdition ?? CountryEdition.Regular
  )
  const [newGradingCompany, setNewGradingCompany] = useState(initialAttributes.gradingCompany)
  const [newGrade, setNewGrade] = useState(initialAttributes.grade)

  const [locationName, setLocationName] = useState(location.name)
  const [editedClassStyleOnRow, setEditedClassStyleOnRow] = useState('')
  const [valuesHaveBeenChanged, setValuesHaveBeenChanged] = useState(false)
  const [disableChangeQuantity, setDisableChangeQuantity] = useState(false)
  const [disableRow, setDisableRow] = useState(false)
  const [openModifyGradingPanel, setOpenModifyGradingPanel] = useState(false)

  const languageRef = useRef<EditLanguageCellRef>(null)
  const conditionRef = useRef<EditConditionCellRef>(null)
  const signedRef = useRef<EditExtraAttributesCellRef>(null)
  const firstEdRef = useRef<EditExtraAttributesCellRef>(null)
  // const gradingRef = useRef<EditGradingCellRef>(null)

  const editCardsQuantityOptions = [
    ...Array.from({ length: location.quantity }, (_, i) => `${i + 1}`),
  ]

  const { currentValues, setCurrentValues, resetLocationRows, setResetLocationRows } =
    useLocationStore((state) => state)

  const languageLabel = CARD_LANGUAGES_WITH_FLAGS.find(
    (lang) => lang.value === initialAttributes.idLanguage
  )

  useEffect(() => {
    if (resetLocationRows) {
      setDeltaQuantity(0)
      setQuantityToEdit(location.quantity)
      setLocationName(location.name)
      setResetLocationRows(false)

      languageRef?.current?.resetValue()
      conditionRef?.current?.resetValue()
      if (finishTypes.length) setNewFinishType(article.finishType || FINISH_TYPE_LABELS.REGULAR)
      if (gameInformations.countryEdition.length) setNewCountryEdition(article.countryEdition)
      if (displayCellOrDoActions(CardExtraAttribute.Signed)) signedRef!.current!.resetValue()
      if (displayCellOrDoActions(CardExtraAttribute.FirstEd)) firstEdRef!.current!.resetValue()
      if (article.graded.isGraded) {
        setNewGradingCompany(initialAttributes.gradingCompany)
        setNewGrade(initialAttributes.grade)
      }
    }
    setValuesHaveBeenChanged(false)
    if (!article.graded.isGraded) setDisableChangeQuantity(false)
  }, [resetLocationRows])

  useEffect(() => {
    const newCurrentValues: ChangesInterface = { ...currentValues }
    newCurrentValues[rowId + location.name] = {
      quantity: quantityToEdit,
      deltaQuantity,
      locationName: locationName,
      condition: newCondition,
      idLanguage: newIdLanguage,
      isFirstEd: newFirstEdition,
      finishType: newFinishType,
      countryEdition: newCountryEdition,
      isSigned: newSigned,
      isNewRow: currentValues[rowId + location.name]?.isNewRow,
      rowId: rowId + location.name,
      gradingCompany: newGradingCompany,
      grade: newGrade,
    }

    if (locationName !== location.name) {
      newCurrentValues[rowId + location.name].locationName = locationName
    }
    setCurrentValues(newCurrentValues)
    checkIfAttributesChanged()
  }, [
    deltaQuantity,
    quantityToEdit,
    newCondition,
    newIdLanguage,
    newFirstEdition,
    newFinishType,
    newCountryEdition,
    newSigned,
    locationName,
    newGradingCompany,
    newGrade,
  ])

  useEffect(() => {
    if (valuesHaveBeenChanged) setEditedClassStyleOnRow('editedRow')
    else if (quantityToEdit === 0) {
      setDisableRow(true)
    } else setEditedClassStyleOnRow('')
  }, [valuesHaveBeenChanged])

  const finishTypes = gameInformations.finishType
    .filter((ft) => ft[isSingle ? 'singles' : 'sealed'])
    .map((ft) => ft.label)

  /**
   *
   * searches the gameInformations map to see if this particular game supports the extra attribute passed as input;
   * if so, then inserts the column to modify this extra attribute; if not, the component will not be displayed.
   */
  const displayCellOrDoActions = (attribute: CardExtraAttribute): boolean => {
    if (!isSingle && attribute != CardExtraAttribute.FirstEd) {
      return false
    }

    if (gameInformations.cardExtraAttribute.some((element) => element.attribute === attribute))
      return true
    return false
  }

  const handleChangeQuantityToEdit = (newValue: any) => {
    setQuantityToEdit(parseInt(newValue))
  }

  const resetAllSingleRow = () => {
    setDeltaQuantity(0)
    setQuantityToEdit(location.quantity)
    setLocationName(location.name)
    languageRef?.current?.resetValue()
    conditionRef?.current?.resetValue()
    if (finishTypes.length) setNewFinishType(article.finishType || FINISH_TYPE_LABELS.REGULAR)
    if (gameInformations.countryEdition.length) setNewCountryEdition(article.countryEdition)
    if (displayCellOrDoActions(CardExtraAttribute.Signed)) signedRef!.current!.resetValue()
    if (displayCellOrDoActions(CardExtraAttribute.FirstEd)) firstEdRef!.current!.resetValue()
    if (article.graded.isGraded) {
      setNewGradingCompany(initialAttributes.gradingCompany)
      setNewGrade(initialAttributes.grade)
    }
  }

  function checkIfAttributesChanged(): void {
    if (quantityToEdit !== location.quantity && !disableChangeQuantity) {
      setDisableChangeQuantity(true)
      return
    }
    if (deltaQuantity !== initialAttributes.deltaQuantity) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(false)
      return
    }
    if (
      newCondition !== initialAttributes.condition ||
      newIdLanguage !== initialAttributes.idLanguage
    ) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(true)
      return
    }

    if (
      newSigned !== initialExtraAttributes.isSigned ||
      newFirstEdition !== initialExtraAttributes.isFirstEd
    ) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(true)
      return
    }

    if (newFinishType !== initialAttributes.finishType) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(true)
      return
    }

    if (newCountryEdition !== initialAttributes.countryEdition) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(true)
      return
    }

    if (locationName !== location.name) {
      setValuesHaveBeenChanged(true)
      setDisableChangeQuantity(true)
      return
    }

    if (newGradingCompany !== initialAttributes.gradingCompany) {
      setValuesHaveBeenChanged(true)
      return
    }

    if (newGrade !== initialAttributes.grade) {
      setValuesHaveBeenChanged(true)
      return
    }

    setValuesHaveBeenChanged(false)
    if (!article.graded.isGraded) setDisableChangeQuantity(false)
    return
  }

  /****************/

  const subtractDeltaQuantity = (disabled: boolean) => {
    if (!disabled) {
      if (location.quantity + deltaQuantity > 0) {
        setDeltaQuantity((deltaQuantity) => deltaQuantity - 1)
      }
    }
  }

  const addDeltaQuantity = (disabled: boolean) => {
    if (!disabled && !article.graded.isGraded) {
      setDeltaQuantity((deltaQuantity) => deltaQuantity + 1)
    }
  }
  const { t } = useTranslation()

  const handleNewGrading = (newGrading: { gradingCompany: string; grade: string }) => {
    setNewGradingCompany(newGrading.gradingCompany)
    setNewGrade(newGrading.grade)
    setOpenModifyGradingPanel(false)
  }

  return (
    <>
      <tr key={location.name} className={`${editedClassStyleOnRow} location-panel-row`}>
        {/* quantity */}
        <td align="center">
          <Typography data-testid={`quantity-${locationName}`}>{location.quantity}</Typography>
        </td>
        {/* change quantity */}
        <td align="center">
          <div
            className={`d-flex flex-row justify-content-center align-items-center ${
              disableChangeQuantity ? 'disabled-cell' : ''
            }`}
          >
            <i
              data-testid={`decrease-quantity-${locationName}`}
              className="fa-light fa-circle-minus interactions-button"
              onClick={() => subtractDeltaQuantity(disableChangeQuantity)}
            ></i>
            <Typography data-testid={`change-quantity-${locationName}`} className="mx-2">
              {deltaQuantity}
            </Typography>
            <i
              data-testid={`increase-quantity-${locationName}`}
              className="fa-light fa-circle-plus interactions-button"
              onClick={() => addDeltaQuantity(disableChangeQuantity)}
            ></i>
          </div>
        </td>

        {/* edit quantity */}
        <td width={65} align="center">
          <Autocomplete
            className="locations-panel"
            data-testid={`edit-quantity-${locationName}`}
            id="number-autocomplete"
            disableClearable
            size="small"
            disabled={deltaQuantity !== 0 || disableRow}
            options={editCardsQuantityOptions}
            value={quantityToEdit}
            getOptionLabel={(option) => option.label || option.toString()}
            onChange={(_event, newValue) => handleChangeQuantityToEdit(newValue)}
            renderInput={(params) => <TextField {...params} />}
            renderOption={(option) => {
              return <li data-testid={`select-edit-quantity-${option}`}>{option.toString()}</li>
            }}
          />
        </td>

        {/* language */}
        <td width={120} align="center">
          <EditLanguageCell
            ref={languageRef}
            locationName={locationName}
            oldValue={languageLabel!.value}
            setNewIdLanguage={setNewIdLanguage}
            disabled={deltaQuantity !== 0 || disableRow}
          />
        </td>

        {/* condition */}
        {isSingle ? (
          <td width={120} align="center">
            <EditConditionCell
              ref={conditionRef}
              locationName={locationName}
              oldValue={initialAttributes.condition}
              setNewCondition={setNewCondition}
              disabled={deltaQuantity !== 0 || disableRow}
            />
          </td>
        ) : (
          <></>
        )}

        {/* attributes games */}
        {/* finishType */}

        {finishTypes.length > 0 && (
          <td width={120} align="center">
            <Select
              fullWidth
              className="locations-panel"
              data-testid={`${FINISH_TYPE_LABELS.FOIL}-${locationName}`}
              value={newFinishType}
              onChange={(e) => {
                setNewFinishType(e.target.value as FINISH_TYPE_LABELS)
              }}
            >
              {finishTypes.map((attr) => (
                <MenuItem key={attr} value={attr}>
                  {attr.replace(/^is/, '')}
                </MenuItem>
              ))}
            </Select>
          </td>
        )}

        {gameInformations.countryEdition.length > 0 && (
          <td width={120} align="center">
            <Select
              fullWidth
              className="locations-panel"
              data-testid={`countryEdition-${locationName}`}
              value={newCountryEdition}
              onChange={(e) => {
                setNewCountryEdition(e.target.value as CountryEdition)
              }}
            >
              {countryEditionMap.map((entry) => (
                <MenuItem key={entry.code} value={entry.value}>
                  {entry.label}
                </MenuItem>
              ))}
            </Select>
          </td>
        )}

        {/* signed */}
        {displayCellOrDoActions(CardExtraAttribute.Signed) ? (
          <td width={120} align="center">
            <EditExtraAttributeCell
              key={`${locationName}-${CardExtraAttribute.Signed}`}
              ref={signedRef}
              currentAttribute={CardExtraAttribute.Signed}
              locationName={locationName}
              oldValue={initialExtraAttributes.isSigned}
              setNewAttribute={setNewSigned}
              disabled={deltaQuantity !== 0 || disableRow}
              gameInformations={gameInformations}
            />
          </td>
        ) : null}

        {/* first edition */}
        {displayCellOrDoActions(CardExtraAttribute.FirstEd) ? (
          <td width={120} align="center">
            <EditExtraAttributeCell
              key={`${locationName}-${CardExtraAttribute.FirstEd}`}
              ref={firstEdRef}
              currentAttribute={CardExtraAttribute.FirstEd}
              locationName={locationName}
              oldValue={initialExtraAttributes.isFirstEd}
              setNewAttribute={setNewFirstEdition}
              disabled={deltaQuantity !== 0 || disableRow}
              gameInformations={gameInformations}
            />
          </td>
        ) : null}

        {article.graded.isGraded && (
          <td width={100} align="center">
            <Button
              onClick={() => setOpenModifyGradingPanel(true)}
              data-testid="update-grading"
              variant="outlined"
            >
              {newGradingCompany} - {newGrade}
            </Button>
          </td>
        )}

        {/* location */}
        <td width={130} align="center">
          <TextField
            className="locations-panel"
            disabled={deltaQuantity !== 0 || disableRow}
            data-testid={`location-${locationName}`}
            value={locationName}
            placeholder="Default" // show 'Default' as placeholder
            onChange={(e) => setLocationName(e.target.value)}
            // onBlur={() => {
            //   if (locationName.trim() === '') {
            //     setLocationName('Default')
            //   }
            // }}
            onFocus={() => {
              if (locationName === 'Default') {
                setLocationName('') // when it receives focus it whitens the field if it is 'Default'
              }
            }}
            style={{ width: '100px', textAlignLast: 'center' }}
          />
        </td>

        {/*reset button */}
        <td width={140} align="center">
          <CustomTooltip title={t('common.reset') as string}>
            <i
              className={`fa-light fa-arrow-rotate-left table-action-icon ${
                disableRow && 'disabled'
              }`}
              onClick={() => resetAllSingleRow()}
            ></i>
          </CustomTooltip>
        </td>
      </tr>

      {openModifyGradingPanel && (
        <tr>
          <td colSpan={20}>
            <div style={{ maxWidth: '520px' }} className="mb-3">
              <GradingPanel
                initialGradingCompany={initialAttributes.gradingCompany!}
                initialGrade={initialAttributes.grade!}
                handleNewGrading={handleNewGrading}
                setOpenModifyGradingPanel={setOpenModifyGradingPanel}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

interface GradingPanelProps {
  initialGradingCompany: string
  initialGrade: string
  handleNewGrading: (newGrading: { gradingCompany: string; grade: string }) => void
  setOpenModifyGradingPanel: React.Dispatch<React.SetStateAction<boolean>>
}

export const GradingPanel: React.FC<GradingPanelProps> = ({
  initialGradingCompany,
  initialGrade,
  handleNewGrading,
  setOpenModifyGradingPanel,
}) => {
  const [gradingCompany, setGradingCompany] = useState(initialGradingCompany)
  const { t } = useTranslation()
  const { gradesFor, loading } = useGradingScale()
  const [grade, setGrade] = useState('')

  const options = gradesFor(gradingCompany)
  const initialGradeStillExists = options.includes(initialGrade)

  // The list arrives after the panel is on screen, so the grade the card already has can only be
  // preselected once it is there. When it is not in the list — a legacy "8", or a price typed into the
  // field years ago — the field stays empty and the old value is shown next to it: the seller either
  // replaces it with a grade the company really issues, or closes the panel and nothing is written.
  // Changing company clears the field too, since a grade one company issues another may not.
  useEffect(() => {
    setGrade(
      gradingCompany === initialGradingCompany && initialGradeStillExists ? initialGrade : ''
    )
  }, [gradingCompany, initialGradeStillExists])

  return (
    <div style={{ maxWidth: '500px', margin: '0 auto' }} className="mt-2 container-fluid">
      <div className="row d-flex flex-row align-items-center">
        <div className="col-lg-6 text-start">
          <FormLabel>{t('stockPricing.gradingCompany')}</FormLabel>
        </div>
        <div className="col-lg-6">
          <Filter
            type="select"
            defaultValue={gradingCompany}
            menuitems={[
              <MenuItem style={{ zIndex: 9999, position: 'relative' }} key={''} value={''}>
                {''}
              </MenuItem>,

              ...GRADING_COMPANY_LABELS.map((provider) => (
                <MenuItem
                  style={{ zIndex: 9999, position: 'relative' }}
                  key={provider.code}
                  value={provider.code}
                >
                  {provider.code} - {provider.label}
                </MenuItem>
              )),
            ]}
            setFilter={(_name, newValue) => setGradingCompany(newValue)}
            label=""
            name={'grading-provider'}
          />
        </div>
      </div>
      <div className="row d-flex flex-row align-items-center">
        <div className="col-lg-6 text-start">
          <FormLabel>{t('stockPricing.grade')}</FormLabel>
        </div>
        <div className="col-lg-6">
          <Autocomplete
            options={options}
            value={grade === '' ? null : grade}
            disabled={gradingCompany === '' || loading}
            // Typing the grade and pressing enter is enough: with hundreds of graded cards to go
            // through, reaching for the mouse to confirm every one of them is the slow part.
            autoHighlight
            onChange={(_event, newValue) => setGrade(newValue ?? '')}
            // The testid sits on the input rather than on the root, so the system tests keep finding
            // the field the way they did when this was a plain text field.
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('inventory.insertGradeValue')}
                inputProps={{ ...params.inputProps, 'data-testid': 'grade' }}
              />
            )}
            renderOption={(option) => <li data-testid={`select-grade-${option}`}>{option}</li>}
          />
          {initialGrade !== '' && !initialGradeStillExists && (
            <small>{t('stockPricing.currentGrade', { grade: initialGrade })}</small>
          )}
        </div>
      </div>
      <div className="w-100 d-flex justify-content-end mt-5">
        <div className="me-3">
          <Button
            variant="outlined"
            data-testid="cancel-grading"
            onClick={() => {
              setOpenModifyGradingPanel(false)
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
        <Button
          color="primary"
          variant="contained"
          data-testid="confirm-grading"
          disabled={grade === ''}
          onClick={() => handleNewGrading({ gradingCompany, grade })}
        >
          {t('common.confirm')}
        </Button>
      </div>
    </div>
  )
}
